import { TimeRangeConfig, TimeRangeKind, ReportChart } from '@/api/generated/model';
import { formatToLocaleDateValue, getTimezoneName, subtractDays } from './date.util';
import { getDefaultTimeRange } from '@/api/generated/interactions/interactions';

export const getUnboundedTimeRange = (): TimeRangeConfig => ({
  kind: TimeRangeKind.custom,
  start: new Date(0).toISOString(),
  end: new Date().toISOString(),
  timezone: getTimezoneName(),
  recompute: false,
});

export const getTimeRangeConfigDefault = (): TimeRangeConfig => {
  const today = new Date().getTime();
  const start = subtractDays(today, 7).valueOf();

  return {
    kind: TimeRangeKind['7_days'],
    start: formatToLocaleDateValue(start),
    end: formatToLocaleDateValue(today),
    timezone: getTimezoneName(),
    recompute: true,
  };
};

export const getTimeRangeConfigDefaultAsync = async (projectId?: string): Promise<TimeRangeConfig> => {
  const today = new Date().getTime();
  const start = subtractDays(today, 7).valueOf();

  // Define a default time range
  let timeRange: TimeRangeConfig = {
    kind: TimeRangeKind['7_days'],
    start: formatToLocaleDateValue(start),
    end: formatToLocaleDateValue(today),
    timezone: getTimezoneName(),
    recompute: true,
  };

  // Only make the API call if projectId is available
  if (projectId) {
    try {
      // Await the response from your API call
      const response = await getDefaultTimeRange(projectId);
      if (response) {
        // Update timeRange with the data from API
        timeRange = {
          kind: response.kind as TimeRangeKind,
          start: formatToLocaleDateValue(new Date(response.start).valueOf()),
          end: response.end ? formatToLocaleDateValue(new Date(response.end).getTime()) : undefined,
          timezone: getTimezoneName(),
          recompute: true,
        };
      }
    } catch (error) {
      console.error('Failed to fetch default time range:', error);
    }
  }

  return timeRange;
};

/**
 * Get the duration in days for a given TimeRangeKind
 */
function getTimeRangeDurationInDays(kind: TimeRangeKind): number {
  switch (kind) {
    case TimeRangeKind.today:
    case TimeRangeKind.yesterday:
      return 1;
    case TimeRangeKind['7_days']:
      return 7;
    case TimeRangeKind['30_days']:
      return 30;
    case TimeRangeKind['3_months']:
    case TimeRangeKind['3_months_full']:
      return 90;
    case TimeRangeKind['6_months']:
    case TimeRangeKind['6_months_full']:
      return 180;
    case TimeRangeKind['12_months']:
    case TimeRangeKind['12_months_full']:
    case TimeRangeKind.this_year:
      return 365;
    case TimeRangeKind.this_month:
    case TimeRangeKind.last_month:
      return 30; // approximate
    case TimeRangeKind.this_quarter:
    case TimeRangeKind.last_quarter:
    case TimeRangeKind.q1:
    case TimeRangeKind.q2:
    case TimeRangeKind.q3:
    case TimeRangeKind.q4:
      return 90; // approximate
    case TimeRangeKind.ytd:
      return Math.ceil(
        (new Date().getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) / (24 * 60 * 60 * 1000),
      );
    case TimeRangeKind.custom:
    default:
      return 7; // fallback to 7 days
  }
}

/**
 * Get the highest (longest) time range among all charts in a report
 */
export function getHighestTimeRangeFromCharts(charts: ReportChart[]): TimeRangeConfig {
  if (!charts?.length) {
    return getTimeRangeConfigDefault();
  }

  let highestTimeRange: TimeRangeConfig = getTimeRangeConfigDefault();
  let maxDuration = 7; // default 7 days

  for (const chart of charts) {
    if (chart.configuration?.time_range) {
      const timeRange = chart.configuration.time_range;

      if (timeRange.kind === TimeRangeKind.custom) {
        if (timeRange.start && timeRange.end) {
          const startDate = new Date(timeRange.start);
          const endDate = new Date(timeRange.end);
          const duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));

          if (duration > maxDuration) {
            maxDuration = duration;
            highestTimeRange = timeRange;
          }
        }
      } else {
        const duration = getTimeRangeDurationInDays(timeRange.kind);

        if (duration > maxDuration) {
          maxDuration = duration;
          highestTimeRange = timeRange;
        }
      }
    }
  }

  return highestTimeRange;
}

// export const updateTimeRange = (timeRange: TimeRangeConfig, updateEnd = true) => {
//   const now = updateEnd && timeRange.kind !== 'custom' ? new Date().toISOString() : timeRange.end;
//   const kind = timeRange.kind;

//   let start = timeRange.start;

//   if (kind === 'today') {
//     start = startOfDay(now).toISOString();
//   } else if (kind === '7_days') {
//     start = subtractDays(now, 7).toDate().toISOString();
//   } else if (kind === '30_days') {
//     start = subtractDays(now, 30).toDate().toISOString();
//   } else if (kind === 'this_month') {
//     start = startOfMonth(now).toISOString();
//   } else if (kind === '12_months') {
//     start = subtractDays(now, 365).toDate().toISOString();
//   } else if (kind == '6_months') {
//     start = subtractDays(now, 180).toDate().toISOString();
//   } else if (kind === 'ytd') {
//     start = startOfYear(now).toISOString();
//   } else if (kind === 'this_quarter') {
//     const currentDate = new Date(now);
//     const month = currentDate.getMonth();
//     const quarterStartMonth = Math.floor(month / 3) * 3;
//     const quarterStartDate = new Date(currentDate.getFullYear(), quarterStartMonth, 1);
//     start = quarterStartDate.toISOString();
//   }
//   return { ...timeRange, start, end: now } satisfies TimeRangeConfig;
// };
//
// export const monthsMap: { [k: number]: string } = {
//   1: 'January',
//   2: 'February',
//   3: 'March',
//   4: 'April',
//   5: 'May',
//   6: 'June',
//   7: 'July',
//   8: 'August',
//   9: 'September',
//   10: 'October',
//   11: 'November',
//   12: 'December',
// };
//
// export function divideInMonths(start: Date, end: Date): { start: Date; end: Date }[] {
//   // returns all the time ranges withing a time range (start, end) divided by months
//   // the time range can span multiple years
//   return eachMonthOfInterval({ start, end }).map((d) => {
//     const lower = max([d, start]);
//     const upper = min([endOfMonth(d), end]);
//
//     return { start: lower, end: upper };
//   });
// }
